import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import "./edit.css";
import AdminLayout from "./AdminLayout";

const EditProperty = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    price: "",
    transactionType: "",
    productType: "",
    propertyCategory: "residential",
    address: "",
    city: "",
    features: {
      bedrooms: 0,
      bathrooms: 0,
      parkingSpaces: 0,
      garages: 0,
      hasPool: false,
      isWheelchairAccessible: false,
      isWaterfront: false,
      hasNavigableWater: false,
      allowsPets: false,
      allowsSmoking: false,
    },
    images: [], // Initialiser vide
    buildingDetails: {
      yearBuilt: "",
      isNewConstruction: false,
      isHistorical: false,
      structureType: "Plain-pied",
    },
    description: "",
    agent: "", // Stocker l'ID de l'agent
    moveInDate: "",
    isForeclosure: false,
  });

  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  // Charger la propriété existante
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`https://keurgui-backend-kjour.ondigitalocean.app/api/products/${id}`);
        // Adapter selon le backend : vérifier le nom de la clé de l'agent
        setFormData({
          ...response.data,
          agent: response.data.agent?._id || "" // ou 'agentId' selon backend
        });
      } catch (error) {
        console.error("Erreur lors de la récupération du produit :", error);
      }
    };
    fetchProperty();
  }, [id]);

  // Charger les agents pour le select
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await axios.get("https://keurgui-backend-kjour.ondigitalocean.app/agents");
        setAgents(res.data);
        setLoadingAgents(false);
      } catch (err) {
        setAgents([]);
        setLoadingAgents(false);
      }
    };
    fetchAgents();
  }, []);

  // Gestion des champs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gestion des champs imbriqués
  const handleNestedChange = (e, section) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  // Upload d'image
  const handleLocalUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formDataFile = new FormData();
    formDataFile.append("image", file);

    try {
      const res = await axios.post("https://keurgui-backend-kjour.ondigitalocean.app/api/upload", formDataFile, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const imageUrl = res.data.imageUrl;
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));
    } catch (err) {
      alert("Upload échoué");
      console.error(err);
    }
  };

  // Supprimer une image
  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`https://keurgui-backend-kjour.ondigitalocean.app/api/products/${id}`, formData);
      alert("Produit modifié avec succès");
      navigate("/admin");
    } catch (error) {
      console.error("Erreur lors de la modification :", error.response || error);
      alert("Échec de la modification du produit");
    }
  };

  return (
    <AdminLayout>
    <div className="edit-property-container">
      <h2>Modifier le produit</h2>
      <form onSubmit={handleSubmit}>
        <div>
          <label>Titre :</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} required />
        </div>
        <div>
          <label>Prix :</label>
          <input type="number" name="price" value={formData.price} onChange={handleChange} required />
        </div>
        <div>
          <label>Type de transaction :</label>
          <select name="transactionType" value={formData.transactionType} onChange={handleChange} required>
            <option value="">Sélectionnez</option>
            <option value="sale">À vendre</option>
            <option value="rent">À louer</option>
          </select>
        </div>
        <div>
          <label>Type de produit :</label>
          <select
    name="productType"
    value={formData.productType}
    onChange={handleChange}
    required
  >
    <option value="appartement">Appartement</option>
    <option value="bureau_commerce">Bureau / Commerce</option>
    <option value="hotel_restaurant">Hôtel / Restaurant</option>
    <option value="immeuble">Immeuble</option>
    <option value="residence">Résidence</option>
    <option value="studio_chambre">Studio / Chambre</option>
    <option value="villa_maison">Villa / Maison</option>
    <option value="terrain">Terrain</option>
  </select>
        </div>
        <div>
          <label>Catégorie de propriété :</label>
          <select name="propertyCategory" value={formData.propertyCategory} onChange={handleChange} required>
            <option value="residential">Résidentiel</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>
        <div>
          <label>Adresse :</label>
          <input type="text" name="address" value={formData.address} onChange={handleChange} required />
        </div>
        <div>
          <label>Ville :</label>
          <input type="text" name="city" value={formData.city} onChange={handleChange} required />
        </div>

        {/* Gestion des images (upload et suppression) */}
        <div>
          <label>Ajouter une image :</label>
          <input type="file" accept="image/*" onChange={handleLocalUpload} />
          {formData.images.map((imgUrl, i) => (
            <div key={i} style={{display: "flex", alignItems: "center", marginBottom: 6}}>
              <img src={imgUrl} alt={`img-${i}`} width={100} style={{marginRight: 8}} />
              <button type="button" onClick={() => handleRemoveImage(i)}>Supprimer</button>
            </div>
          ))}
        </div>

        <div>
          <label>Chambres :</label>
          <input type="number" name="bedrooms" value={formData.features.bedrooms} onChange={(e) => handleNestedChange(e, "features")} />
        </div>
        <div>
          <label>Salles de bain :</label>
          <input type="number" name="bathrooms" value={formData.features.bathrooms} onChange={(e) => handleNestedChange(e, "features")} />
        </div>
        <div>
          <label>Description :</label>
          <textarea name="description" value={formData.description} onChange={handleChange} />
        </div>
        {/* Sélection/modification d'agent */}
        <div>
          <label>Agent :</label>
          {loadingAgents ? (
            <p>Chargement des agents...</p>
          ) : (
            <select name="agent" value={formData.agent} onChange={handleChange} required>
              <option value="">Sélectionnez un agent</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.agentName}
                </option>
              ))}
            </select>
          )}
        </div>
        <button type="submit">Enregistrer</button>
      </form>
    </div>
    </AdminLayout>
  );
};

export default EditProperty;